import { useState } from "react";
import classNames from "classnames";
import ObjectsBox from "../Objects";
import { copyToClipboard } from "helpers/utility/utilities";
import TooltipWrapper from "views/uikit/TooltipWrapper";
import LinkIcon from "assets/icons/LinkIcon";
import styles from "./style.module.scss";
import { Tooltip } from "antd";
import moment from "moment";
import ContentBodyLoader from "../../../../../components/MetadataSummary/Content/ContentBody/ContentBodyLoader";

type ImageInfo = {
  Height?: number;
  Width?: number;
};

const GeneralBox = ({
  img,
  imageLabels,
  objectLables,
  selectedLabels,
  uniquenessData,
  metadataLinks,
  imageUniquenessScore,
  isLoading = false,
}: any) => {
  const [copiedText, setCopiedText] = useState("");

  const objectUniquenessScores: any[] =
    uniquenessData
      ?.filter((data: any) => !!data.objectId)
      .map((data: any) => {
        return {
          ...data,
          uniquenessScore: data.uniquenessScore,
        };
      }) || [];

  let imgInfo: ImageInfo = {
    Height: img?.height,
    Width: img?.width,
  };

  const copyIconClassnames = classNames(styles.copyIcon, {
    [styles.defaultCursor]: copiedText,
  });

  const groupedInfo: { key: string; value: string | number }[][] =
    Object.entries(imgInfo).reduce(
      (
        acc: { key: string; value: string | number }[][],
        [key, value],
        index,
      ) => {
        const pairIndex = Math.floor(index / 2);
        acc[pairIndex] = acc[pairIndex] || [];
        acc[pairIndex].push({ key, value });
        return acc;
      },
      [],
    );

  const isSmalltext = (key: string) => {
    let value = key?.toLowerCase();
    switch (value) {
      case "file name":
        return true;
      case "file size":
        return true;
      case "height":
        return true;
      default:
        return false;
    }
  };

  const handleCopyUrlClick = (text: string) => {
    copyToClipboard(text)
      .then(() => {
        setCopiedText(text);
        setTimeout(() => setCopiedText(""), 3000);
      })
      .catch((err) => console.error(err));
  };

  return (
    <div className={styles.generalContainer}>
      {isLoading ? (
        <ContentBodyLoader center size={48} />
      ) : (
        img && (
          <div className={styles.infoContainer}>
            <div className={styles.infoRow}>
              <div className={styles.fileNamePathContainer}>
                <div className={styles.fileNameContainer}>
                  <span className={styles.fileNameLabel}>File Name</span>
                  <span className={styles.fileNameValue} title={img?.file_name}>
                    {img?.file_name}
                  </span>
                </div>
                <div className={styles.pathPair}>
                  <span className={styles.path}>Path</span>
                  <span className={styles.pathText} title={img?.original_uri}>
                    <span className={styles.imageUri}>{img?.original_uri}</span>

                    <Tooltip title={copiedText ? "Copied!" : "Copy URL"}>
                      <div
                        className={copyIconClassnames}
                        onClick={() =>
                          handleCopyUrlClick(img.original_uri || "")
                        }
                      >
                        <LinkIcon clicked={copiedText === img.original_uri} />
                      </div>
                    </Tooltip>
                  </span>
                </div>
              </div>
            </div>
            {groupedInfo.map((pair, index) => (
              <div className={styles.infoRow} key={index}>
                {pair.map(({ key, value }, index) => (
                  <div className={styles.pair} key={index}>
                    <span
                      className={styles.infoTitle}
                      style={
                        isSmalltext(key) ? { width: "60px" } : { width: "76px" }
                      }
                    >
                      {key}
                    </span>
                    <span className={styles.infoDescription}>{value}</span>
                  </div>
                ))}
              </div>
            ))}
            {typeof imageUniquenessScore === "number" && (
              <div className={styles.infoRow}>
                <div className={classNames(styles.pair, styles.fullWidth)}>
                  <span className={styles.infoTitle}>Uniqueness Score</span>
                  <span className={styles.infoDescription}>
                    {imageUniquenessScore}
                  </span>
                </div>
              </div>
            )}
            {metadataLinks.length > 0 &&
              metadataLinks.map((metadataLink: string, index: number) => {
                const infoTitle = `Link${
                  metadataLinks.length > 1 ? ` ${index + 1}` : ""
                }`;

                return (
                  <div
                    className={styles.infoRow}
                    key={`metadata-link-${index}`}
                  >
                    <div className={classNames(styles.pair, styles.fullWidth)}>
                      <span className={styles.infoTitle}>{infoTitle}</span>
                      <div className={styles.linkContainer}>
                        <span
                          className={styles.metadataLink}
                          title={metadataLink}
                        >
                          {metadataLink}
                        </span>
                        <div
                          className={copyIconClassnames}
                          data-tooltip-id={"image-view-url-button-tooltip"}
                          data-tooltip-content={
                            copiedText ? "Copied!" : "Copy URL"
                          }
                          title=""
                          onClick={() => handleCopyUrlClick(metadataLink)}
                        >
                          <LinkIcon clicked={copiedText === metadataLink} />

                          <TooltipWrapper id="image-view-url-button-tooltip" />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )
      )}
      {imageLabels?.length > 0 && (
        <div className={styles.imageLabelsContainer}>
          <div className={styles.imageLabelTitle}>Image labels</div>
          <div className={styles.imageLabels}>
            {imageLabels?.map((singleLabel: string, index: number) => (
              <div key={index} className={styles.imageLabel}>
                {singleLabel}
              </div>
            ))}
          </div>
        </div>
      )}
      {objectLables && objectLables.length > 0 && (
        <div className={styles.objectLabelsContainer}>
          <ObjectsBox
            labels={objectLables}
            selectedLabels={selectedLabels}
            uniquenessScores={objectUniquenessScores}
          />
        </div>
      )}
      {img?.created_at && (
        <div className={styles.createdByContainer}>
          <div className={classNames(styles.pair, styles.fullWidth)}>
            <span className={styles.infoTitle}>Insertion time</span>
            <span className={styles.infoDescription}>
              {moment
                .utc(img?.created_at)
                .local()
                .format("MMMM D, YYYY [at] h:mm A")}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneralBox;
